import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useGetGameMapQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsInCreatorMode, selectMapData, selectSelectedMarkerId, setPlayerLocationDeltas } from '@repo/store/maps/slices/mapData';
import type { MapDataGameCoordinateDeltas, MapDataGameCoordinates, PlayerLocationReferencePoint } from '@repo/types/maps/map-types';
import { calculatePlayerLocationDeltas } from '@repo/types/maps/map-utils';
import { updateMapMetadata } from '@repo/wemod-api-client/maps-api-client';
import { useCallback, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { usePlayerLocation } from '../../game-map/hooks/usePlayerLocation';
const MAX_REFERENCE_POINTS = 2;
const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const isValidGameCoords = (coords: unknown): coords is MapDataGameCoordinates => !!coords && isFiniteNumber((coords as MapDataGameCoordinates).x) && isFiniteNumber((coords as MapDataGameCoordinates).y) && isFiniteNumber((coords as MapDataGameCoordinates).z);
export const PlayerLocationTools = () => {
  const [xMapping, setXMapping] = useState<'x' | 'y' | 'z'>('x');
  const [yMapping, setYMapping] = useState<'x' | 'y' | 'z'>('y');
  const [zMapping, setZMapping] = useState<'x' | 'y' | 'z'>('z');
  const [trackedPoints, setTrackedPoints] = useState<PlayerLocationReferencePoint[]>([]);
  const [calculatedDeltas, setCalculatedDeltas] = useState<MapDataGameCoordinateDeltas | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    t
  } = useT(['game_map']);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const dispatch = useAppDispatch();
  const {
    deltas
  } = usePlayerLocation();
  const {
    refetch,
    isFetching
  } = useGetGameMapQuery({
    externalId: externalMapId ?? '',
    debug: isInCreatorMode
  }, {
    skip: !externalMapId
  });
  useEffect(() => {
    if (deltas) {
      const {
        deltas: deltasData,
        referencePoints
      } = deltas;
      setXMapping(deltasData.gameCoordsMap?.x ?? 'x');
      setYMapping(deltasData.gameCoordsMap?.y ?? 'y');
      setZMapping(deltasData.gameCoordsMap?.z ?? 'z');
      setTrackedPoints(referencePoints);
      setCalculatedDeltas(deltasData);
    }
  }, [deltas]);
  const selectedLocation = mapData?.geojson.features.find(feature => feature.properties.externalId === selectedMarkerId);
  const hasSelectedPinWithGameCoords = !!(selectedLocation?.properties.gameCoords && isValidGameCoords(selectedLocation.properties.gameCoords));
  const coordinateOptions = [{
    value: 'x' as const,
    label: 'X=X'
  }, {
    value: 'y' as const,
    label: 'X=Y'
  }, {
    value: 'z' as const,
    label: 'X=Z'
  }];
  const handleAddPoint = useCallback(async () => {
    if (!hasSelectedPinWithGameCoords || !selectedLocation) {
      console.warn('No selected pin with game coordinates');
      return;
    }
    try {
      const [lng, lat] = selectedLocation.geometry.coordinates;
      const src = selectedLocation.properties.gameCoords as MapDataGameCoordinates;
      if (!isValidGameCoords(src)) {
        toast({
          title: t($ => $.player_location_tools.toast.invalid_game_coords),
          variant: 'error'
        });
        return;
      }
      const newPoint: PlayerLocationReferencePoint = {
        lngLat: {
          lat,
          lng
        },
        gameCoords: {
          x: src.x,
          y: src.y,
          z: src.z
        }
      };
      setTrackedPoints(prev => {
        if (prev.length >= MAX_REFERENCE_POINTS) return prev;
        return [...prev, newPoint];
      });
    } catch (error) {
      console.error('Failed to add point:', error);
    }
  }, [hasSelectedPinWithGameCoords, selectedLocation, t]);
  useEffect(() => {
    if (trackedPoints.length < MAX_REFERENCE_POINTS) {
      setCalculatedDeltas(null);
      return;
    }
    try {
      const referencePoints_0: PlayerLocationReferencePoint[] = trackedPoints.map(point => ({
        lngLat: point.lngLat,
        gameCoords: point.gameCoords
      }));
      const gameCoordsMap = {
        x: xMapping,
        y: yMapping,
        z: zMapping
      };
      const newDeltas = calculatePlayerLocationDeltas(referencePoints_0, gameCoordsMap);
      if (newDeltas) {
        setCalculatedDeltas(newDeltas);
      }
    } catch (error_0) {
      console.error('Failed to calculate deltas:', error_0);
    }
  }, [trackedPoints, xMapping, yMapping, zMapping]);
  const handleClearDeltas = useCallback(async () => {
    if (!externalMapId) {
      toast({
        title: t($_0 => $_0.player_location_tools.toast.no_map_data),
        variant: 'error'
      });
      return;
    }
    const updatedMetadata = {
      playerLocationDeltas: null
    };
    try {
      await updateMapMetadata(externalMapId, updatedMetadata);
      toast({
        title: t($_2 => $_2.player_location_tools.toast.deltas_cleared),
        variant: 'success'
      });
      refetch?.();
      setCalculatedDeltas(null);
      setTrackedPoints([]);
      setXMapping('x');
      setYMapping('y');
      setZMapping('z');
    } catch (error_1) {
      toast({
        title: t($_1 => $_1.player_location_tools.toast.failed_to_clear),
        description: JSON.stringify(error_1),
        variant: 'error'
      });
    }
  }, [externalMapId, refetch, t]);
  const handleSaveDeltas = useCallback(async () => {
    if (!calculatedDeltas) {
      console.warn('No deltas to save or map data missing');
      return;
    }
    setIsSaving(true);
    try {
      const referencePoints_1: PlayerLocationReferencePoint[] = trackedPoints.map(point_0 => ({
        lngLat: point_0.lngLat,
        gameCoords: point_0.gameCoords
      }));
      const updatedMetadata_0 = {
        playerLocationDeltas: {
          deltas: calculatedDeltas,
          referencePoints: referencePoints_1
        }
      };
      if (!externalMapId) {
        throw new Error('Map external ID not available');
      }
      await updateMapMetadata(externalMapId, updatedMetadata_0);
      toast({
        title: t($_4 => $_4.player_location_tools.toast.deltas_saved),
        variant: 'success'
      });
      refetch?.();
    } catch (error_2) {
      toast({
        title: t($_3 => $_3.player_location_tools.toast.failed_to_save),
        description: JSON.stringify(error_2),
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  }, [calculatedDeltas, externalMapId, trackedPoints, t, refetch]);
  const handleRemovePoint = useCallback((pointIndex: number) => {
    setTrackedPoints(prev_0 => prev_0.filter((_, index) => index !== pointIndex));
  }, []);
  const originalMappings = mapData?.metadata?.playerLocationDeltas?.deltas?.gameCoordsMap;
  const handleResetMappings = useCallback(() => {
    if (originalMappings) {
      setXMapping(originalMappings.x);
      setYMapping(originalMappings.y);
      setZMapping(originalMappings.z);
    }
  }, [originalMappings]);
  const mappingsChanged = originalMappings && (xMapping !== originalMappings.x || yMapping !== originalMappings.y || zMapping !== originalMappings.z);
  const handleTestDeltas = () => {
    if (!calculatedDeltas || !externalMapId) {
      return;
    }
    dispatch(setPlayerLocationDeltas({
      deltas: calculatedDeltas,
      referencePoints: trackedPoints
    }));
  };
  return <>
            <FlexColumn gap="12" width="full">
                <Button onClick={() => setIsInfoModalOpen(true)} color="secondary" size="sm" fullWidth>
                    <Icon icon="info" decorative />
                    {t($_5 => $_5.player_location_tools.learn_about_tools)}
                </Button>

                {!hasSelectedPinWithGameCoords && <Type variant="body-xs" color="gray.200">
                        {t($_6 => $_6.player_location_tools.select_pin_with_game_coords)}
                    </Type>}
                <FlexRow gap="4" wrap="wrap" width="full">
                    <Button variant="filled" onClick={handleAddPoint} color="white" size="sm" disabled={!hasSelectedPinWithGameCoords || trackedPoints.length >= MAX_REFERENCE_POINTS || isFetching} title={!hasSelectedPinWithGameCoords ? t($_7 => $_7.player_location_tools.select_pin_first) : t($_8 => $_8.player_location_tools.add_reference_point)} className={css({
          whiteSpace: 'nowrap',
          flex: '1'
        })}>
                        {hasSelectedPinWithGameCoords ? t($_9 => $_9.player_location_tools.add_current_location) : t($_10 => $_10.add_point)}
                    </Button>
                </FlexRow>

                <FlexColumn gap="12" width="full">
                    <FlexRow gap="4" align="center" justify="between">
                        <FlexRow gap="4" align="center">
                            <Type variant="caption">{t($_11 => $_11.player_location_tools.coordinate_mapping)}</Type>
                            {mappingsChanged && <Type variant="caption" color="orange.600" style={{
              fontSize: '0.7rem'
            }}>
                                    (modified)
                                </Type>}
                        </FlexRow>
                        {mappingsChanged && originalMappings && <Button variant="ghost" onClick={handleResetMappings} color="secondary" size="sm">
                                {t($_12 => $_12.player_location_tools.reset_mappings)}
                            </Button>}
                    </FlexRow>

                    <FlexRow gap="4" align="center" wrap="wrap">
                        <FlexRow gap="4" align="center">
                            <Type variant="caption">X:</Type>
                            <Select value={xMapping} onValueChange={value => setXMapping(value as 'x' | 'y' | 'z')} fullWidth={false} aria-label={t($_13 => $_13.select_x_mapping)} trigger={<Button color="secondary" size="sm" radius="rounded">
                                        {xMapping}
                                    </Button>}>
                                {coordinateOptions.map(option => <SelectItem key={option.value} item={{
                label: option.label,
                value: option.value
              }}>
                                        {option.label}
                                    </SelectItem>)}
                            </Select>
                        </FlexRow>

                        <FlexRow gap="4" align="center">
                            <Type variant="caption">Y:</Type>
                            <Select value={yMapping} onValueChange={value_0 => setYMapping(value_0 as 'x' | 'y' | 'z')} fullWidth={false} aria-label={t($_14 => $_14.select_y_mapping)} trigger={<Button color="secondary" size="sm" radius="rounded">
                                        {yMapping}
                                    </Button>}>
                                {coordinateOptions.map(option_0 => <SelectItem key={option_0.value} item={{
                label: option_0.label.replace('X=', 'Y='),
                value: option_0.value
              }}>
                                        {option_0.label.replace('X=', 'Y=')}
                                    </SelectItem>)}
                            </Select>
                        </FlexRow>

                        <FlexRow gap="4" align="center">
                            <Type variant="caption">Z:</Type>
                            <Select value={zMapping} onValueChange={value_1 => setZMapping(value_1 as 'x' | 'y' | 'z')} fullWidth={false} aria-label={t($_15 => $_15.select_z_mapping)} trigger={<Button color="secondary" size="sm" radius="rounded">
                                        {zMapping}
                                    </Button>}>
                                {coordinateOptions.map(option_1 => <SelectItem key={option_1.value} item={{
                label: option_1.label.replace('X=', 'Z='),
                value: option_1.value
              }}>
                                        {option_1.label.replace('X=', 'Z=')}
                                    </SelectItem>)}
                            </Select>
                        </FlexRow>
                    </FlexRow>

                    <FlexColumn gap="8" width="full">
                        <FlexRow gap="4" align="center">
                            <Type variant="caption">{t($_16 => $_16.player_location_tools.reference_points)}:</Type>
                        </FlexRow>

                        {trackedPoints.length > 0 && <FlexColumn gap="4" width="full">
                                {trackedPoints.map((point_1, index_0) => {
              return <FlexRow key={point_1.lngLat.lng} gap="4" align="center" justify="between" width="full">
                                            <FlexRow gap="4" align="center" style={{
                  flex: 1
                }}>
                                                <Type variant="caption" style={{
                    fontSize: '0.75rem'
                  }}>
                                                    Point {index_0 + 1}: X:{point_1.gameCoords.x.toFixed(2)}, Y:
                                                    {point_1.gameCoords.y.toFixed(2)}, Z:{point_1.gameCoords.z.toFixed(2)}
                                                </Type>
                                            </FlexRow>
                                            <Button variant="ghost" onClick={() => handleRemovePoint(index_0)} color="secondary" size="sm">
                                                {t($_17 => $_17.player_location_tools.remove)}
                                            </Button>
                                        </FlexRow>;
            })}
                            </FlexColumn>}

                        {trackedPoints.length === 0 && <Type variant="body-xs" color="gray.200" style={{
            fontStyle: 'italic'
          }}>
                                {t($_18 => $_18.player_location_tools.no_reference_points)}
                            </Type>}
                    </FlexColumn>

                    {/* Existing Metadata Points Display */}
                    {mapData?.metadata?.playerLocationDeltas?.deltas && <FlexColumn gap="8" width="full">
                            <Type variant="body-xs">{t($_19 => $_19.player_location_tools.existing_metadata)}:</Type>
                            <Type variant="body-xs">
                                {t($_20 => $_20.player_location_tools.scale_x)}{' '}
                                {mapData.metadata.playerLocationDeltas.deltas.scaleX}
                                <br />
                                {t($_21 => $_21.player_location_tools.scale_y)}{' '}
                                {mapData.metadata.playerLocationDeltas.deltas.scaleY}
                                <br />
                                {t($_22 => $_22.player_location_tools.offset_x)}{' '}
                                {mapData.metadata.playerLocationDeltas.deltas.offsetX}
                                <br />
                                {t($_23 => $_23.player_location_tools.offset_y)}{' '}
                                {mapData.metadata.playerLocationDeltas.deltas.offsetY}
                                <br />
                                {t($_24 => $_24.player_location_tools.coords_map)}{' '}
                                {JSON.stringify(mapData.metadata.playerLocationDeltas.deltas.gameCoordsMap)}
                            </Type>
                        </FlexColumn>}

                    {/* Calculated Deltas Display */}
                    {calculatedDeltas && <FlexColumn gap="8" width="full">
                            <Type variant="body-xs">{t($_25 => $_25.player_location_tools.calculated_deltas)}</Type>
                            <Type variant="body-xs">
                                {t($_26 => $_26.player_location_tools.scale_x)} {calculatedDeltas.scaleX}
                                <br />
                                {t($_27 => $_27.player_location_tools.scale_y)} {calculatedDeltas.scaleY}
                                <br />
                                {t($_28 => $_28.player_location_tools.offset_x)} {calculatedDeltas.offsetX}
                                <br />
                                {t($_29 => $_29.player_location_tools.offset_y)} {calculatedDeltas.offsetY}
                                <br />
                                {t($_30 => $_30.player_location_tools.coords_map)}{' '}
                                {JSON.stringify(calculatedDeltas.gameCoordsMap)}
                            </Type>
                        </FlexColumn>}

                    <FlexRow gap="4" justify="end" width="full" wrap="wrap">
                        <Button onClick={handleClearDeltas} color="error" size="sm" disabled={!calculatedDeltas && !mapData?.metadata?.playerLocationDeltas || isSaving || isFetching}>
                            {t($_31 => $_31.player_location_tools.remove_deltas)}
                        </Button>

                        <Button onClick={handleTestDeltas} color="secondary" size="sm" disabled={!calculatedDeltas || isSaving || isFetching}>
                            {t($_32 => $_32.player_location_tools.test_deltas)}
                        </Button>

                        <Button onClick={handleSaveDeltas} color="success" size="sm" disabled={!calculatedDeltas || isSaving || isFetching}>
                            {isSaving ? t($_33 => $_33.player_location_tools.saving) : t($_34 => $_34.player_location_tools.save_deltas)}
                        </Button>
                    </FlexRow>
                </FlexColumn>
            </FlexColumn>
            <Modal isOpen={isInfoModalOpen} onOpenChange={details => setIsInfoModalOpen(details.open)} title={t($_35 => $_35.player_location_tools.modal.title)} size="lg">
                <FlexColumn gap="8" width="full">
                    <Type variant="body-s">{t($_36 => $_36.player_location_tools.modal.intro)}</Type>
                    <Type variant="header-m" color="white">
                        {t($_37 => $_37.player_location_tools.modal.adding_reference_points)}
                    </Type>
                    <Type variant="body-s">
                        <Trans i18nKey={$_38 => $_38.player_location_tools.modal.adding_reference_points_desc} ns="game_map" />
                    </Type>
                    <Type variant="header-m" color="white">
                        {t($_39 => $_39.player_location_tools.modal.xyz_mapping)}
                    </Type>
                    <Type variant="body-s">
                        <Trans i18nKey={$_40 => $_40.player_location_tools.modal.xyz_mapping_desc} ns="game_map" />
                    </Type>
                    <Type variant="header-m" color="white">
                        {t($_41 => $_41.player_location_tools.modal.removing_deltas)}
                    </Type>
                    <Type variant="body-s">{t($_42 => $_42.player_location_tools.modal.removing_deltas_desc)}</Type>
                    <Type variant="header-m" color="white">
                        {t($_43 => $_43.player_location_tools.modal.saving_deltas)}
                    </Type>
                    <Type variant="body-s">{t($_44 => $_44.player_location_tools.modal.saving_deltas_desc)}</Type>
                </FlexColumn>
            </Modal>
        </>;
};